import React from 'react';

import { useEditor } from '@toasttab/sites-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PageType } from 'src/apollo/sites';
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext';

import { useExperiment } from 'shared/components/common/ab_testing/ABTestContext';
import { PopoverContextProvider } from 'shared/components/common/popover/PopoverContext';

import { CART_POPOVER_CONTEXT_KEY } from 'public/components/default_template/online_ordering/cart/CartModal';
import { CartContextProvider } from 'public/components/online_ordering/CartContext';
import { DeliveryContextProvider } from 'public/components/online_ordering/DeliveryContext';
import { FulfillmentContextProvider } from 'public/components/online_ordering/FulfillmentContext';
import { LoyaltyContextProvider } from 'public/components/online_ordering/LoyaltyContext';
import OffersContextProvider from 'public/components/online_ordering/OffersContext';
import { TimeBasedRuleContextProvider } from 'public/components/online_ordering/TimeBasedRuleContext';
import { UpsellContextProvider } from 'public/components/online_ordering/UpsellContext';

export const WRAP_WITH_CART_RELATED_PROVIDERS_DENY_LIST:[String?] = [PageType.Ecommerce];

export const useShouldShowCartInNavigation = (pageType?: PageType) => {
  const { restaurant } = useRestaurant();
  const { isEditor } = useEditor();
  const { ooReusableFlagGolf: ooCartSitesNavEnabled } = useFlags();
  const { selectedVariant: showCartInNavTest } = useExperiment('woo-cart-in-navigation');

  // in the editor we want to give the customer the ability to control the cart in navigation regardless of the test
  const shouldShowCartInNavigationEditor = pageType &&
    !WRAP_WITH_CART_RELATED_PROVIDERS_DENY_LIST.includes(pageType) &&
    restaurant.config?.isOnlineOrderingEnabled &&
    ooCartSitesNavEnabled;
  // on the live sites we want to show the cart in navigation only if the test is in the test variant
  const shouldShowCartInNavigation = shouldShowCartInNavigationEditor && showCartInNavTest === 'test';

  return isEditor ? !!shouldShowCartInNavigationEditor : !!shouldShowCartInNavigation;
};

/**
 * Wraps children with cart related providers
 * used to expose the cart in the navigation that is shared between the order and other sites pages
 * @param shouldShowCartInNavigation is determined by restaurant.config.isOnlineOrderingEnabled && ooCartSitesNavEnabled (featureFlag)
 */
const WrapWithCartRelatedProviders = ({ pageType, children }: { pageType?: PageType, children: React.ReactNode}) => {
  const { isEditor } = useEditor();
  const shouldShowCartInNavigation = useShouldShowCartInNavigation(pageType);

  if(shouldShowCartInNavigation && !isEditor) {
    return (
      <CartContextProvider>
        <TimeBasedRuleContextProvider>
          <DeliveryContextProvider>
            <OffersContextProvider>
              <UpsellContextProvider>
                <LoyaltyContextProvider>
                  <FulfillmentContextProvider>
                    <PopoverContextProvider contextKey={CART_POPOVER_CONTEXT_KEY}>
                      {children}
                    </PopoverContextProvider>
                  </FulfillmentContextProvider>
                </LoyaltyContextProvider>
              </UpsellContextProvider>
            </OffersContextProvider>
          </DeliveryContextProvider>
        </TimeBasedRuleContextProvider>
      </CartContextProvider>
    );
  }
  return children;
};

export default WrapWithCartRelatedProviders;
